/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the hook to use pre-registered patient columns.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to create a context.
 */
import * as React from 'react';

/*
 * Used to type patients and institutions 
 */
import {
    IInstitution,
    OnlinePatientManagementContext,
    PatientsContext,
    useSnackbar
} from '@ngt/opms';

import { AlertTitle, Chip, IconButton, styled, Tooltip, TooltipProps } from '@mui/material';

/*
 * Used to format datetimes
 */
import { DateTime } from 'luxon';

import { GridColDef } from '@mui/x-data-grid';

import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes';

import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';



/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

import { LookupScreeningOutcomeType, Patient, PostUpdateScreeningOutcomeByPatientId } from '../api/dtos';
import ScreeningDialogContext from '../contexts/ScreeningContext';
import { AppSettings } from '../AppSettings';
/*
 * ---------------------------------------------------------------------------------
 * Functions
 * ---------------------------------------------------------------------------------
 */
const StyledChip = styled(Chip)(({theme}) => ({
    backgroundColor: '#987ba7',
    color: theme.palette.common.white,
}));

const StyledButton = styled(IconButton)(({theme}) => ({
    padding: theme.spacing(0),
    marginLeft: theme.spacing(2),
    color: theme.palette.mode === 'dark' ?
        `${AppSettings.palette.primaryDark} !important` :
        `${AppSettings.palette.primaryLight} !important`, 

    '&:hover': {
        color: theme.palette.mode === 'dark' ?
        `${theme.palette.primary.light} !important` :
        `${theme.palette.primary.dark} !important`, 
        background: 'none',
    },

    '&:last-of-type': {
        marginRight: theme.spacing(0),
    }
}));


const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ tooltip: className }} />
))(({ theme }) => ({
    
    backgroundColor: theme.palette.mode === 'dark' ?
        `rgba(0, 0, 0, 0.87)` :
        theme.palette.common.white,
    color: theme.palette.mode === 'dark' ?
        theme.palette.common.white :
        `rgba(0, 0, 0, 0.87)`,
    boxShadow: theme.shadows[1],
    fontSize: 11,
    
}));

const useScreeningGridColDefs = (institutions: IInstitution[] | null, showInitials: boolean, showSite: boolean): GridColDef<Patient>[] => {
    const { setPatientStudyNumber, setScreeningOutcome, setDialogOpen, dialogOpen } = React.useContext(ScreeningDialogContext);

    const { serviceStackClient: client } = React.useContext(OnlinePatientManagementContext);

    const { enqueueSnackbar } = useSnackbar();

    const { refresh: refreshPatients } = React.useContext(PatientsContext);

    const onClick = React.useCallback(async (patient: Patient | null, screeningOutcome: LookupScreeningOutcomeType | null) => {

        if(patient?.fromKoreanSite === true && screeningOutcome === LookupScreeningOutcomeType.Proceed) {
            try {
                await client.post(
                    new PostUpdateScreeningOutcomeByPatientId({
                        patientId: patient?.id,
                        screeningOutcome: LookupScreeningOutcomeType.Proceed
                    })
                );

                refreshPatients();

                enqueueSnackbar(
                    <>
                        <AlertTitle>
                            Screening Result Saved
                        </AlertTitle>
                        Screening result was successfully saved.
                    </>,
                    { variant: 'success' }
                );
            }
            catch (error: any) {

                enqueueSnackbar(
                    <>
                        <AlertTitle>
                            Screening Result Not Saved
                        </AlertTitle>
                        An error occurred while attempting to save the screening result.
                    </>,
                    { variant: 'critical' }
                );
            }
        } else {
            setPatientStudyNumber(patient!.studyNumber);
            setScreeningOutcome(screeningOutcome);
            setDialogOpen(true);
        }
    }, [setPatientStudyNumber, setScreeningOutcome, setDialogOpen, client, enqueueSnackbar, refreshPatients]);

    const columns = React.useMemo(() => {
        const c: (GridColDef<Patient> | boolean)[] = [
            {
                field: 'studyNumber',
                headerName: 'Temp. No',
                width: 140
            },
            showInitials && {
                field: 'initials',
                headerName: 'Initials',
                //width: 200
            },
            {
                field: 'institutionCode',
                headerName: 'Site Code',
                renderCell: rowData => (
                    <StyledChip
                        label={rowData.row.institutionCode}
                        size="small"
                    />
                ),
                width: 110
            },
            {
                field: 'institutionId',
                headerName: 'Institution',
                valueGetter: (_value, row) =>  institutions?.find(i => i.id === row.institutionId)?.name,
                flex: 1
            },
            {
                field: 'enteredDate',
                headerName: 'Date Entered',
                renderCell: rowData => (
                    rowData.row.enteredDate ? DateTime.fromISO(rowData.row.enteredDate).toFormat('dd/MM/yyyy') : null
                ),
                width: 150
            },
            {
                field: 'id',
                headerName: 'Result',
                filterable: false,
                sortable: false,
                renderCell: rowData => {
                    return <>
                        <StyledTooltip title="Start Consent">
                            <StyledButton
                                size="medium"
                                onClick={(e) => {onClick(rowData.row, LookupScreeningOutcomeType.Proceed); e.stopPropagation();}}
                            >
                                <FontAwesomeIcon icon={faCheck} fixedWidth />
                            </StyledButton>
                        </StyledTooltip>
                        <StyledTooltip title="Screen Fail">
                            <StyledButton
                                size="medium"
                                onClick={(e) => {onClick(rowData.row, LookupScreeningOutcomeType.Fail); e.stopPropagation();}}
                            >
                                <FontAwesomeIcon icon={faTimes} fixedWidth />{dialogOpen}
                            </StyledButton>
                        </StyledTooltip>
                    </>
                },
                width: 120
            },
        ];

        return c.filter(column => !!column) as GridColDef<Patient>[];
    }, [showSite, institutions, showInitials, onClick]);

    return columns;
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default useScreeningGridColDefs;